import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState, useCallback } from "react";
import { useColors, useResponsive } from "@inbody/core";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { AccountMenu } from "../account-menu/index.js";
import { SessionTimer } from "../header/components/SessionTimer.js";
import { HEADER_HEIGHT } from "../layout-side-menu-v2/styles/sideMenuStyle.js";
import { UserSearch } from "../user-search/index.js";
import { CorporateChangeButton } from "../header/components/CorporateChangeButton.js";
import { ThemeChangeSwitch } from "../header/components/ThemeChangeSwitch.js";
import { LanguageChangeButton } from "../header/components/LanguageChangeButton.js";
function LayoutHeader({
  onMenuToggle,
  showUserSearch = false,
  userSearchProps,
  showThemeSwitch = true,
  showLanguageChange = true,
  showAccountMenu = true,
  showCorporateChange = true,
  showSessionTimer = false,
  showCopyright = true,
  accountMenuShowProfile = false,
  leftActions,
  rightActions,
  mobileCollapsible = true,
  breadcrumbText,
  userDisplayName,
  userRoleLabel,
  accountMenuExtraItems,
  onOpenReleaseNotes,
  manuals
}) {
  const { gray } = useColors();
  const { isMobile } = useResponsive();
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const handleOpenMore = useCallback(
    (e) => {
      setMoreAnchorEl(e.currentTarget);
    },
    []
  );
  const handleCloseMore = useCallback(() => {
    setMoreAnchorEl(null);
  }, []);
  const hasCollapsibleItems = showThemeSwitch || showLanguageChange || showAccountMenu;
  const useMobileCollapse = isMobile && mobileCollapsible && hasCollapsibleItems;
  return /* @__PURE__ */ jsx(
    AppBar,
    {
      position: "sticky",
      sx: {
        top: 0,
        backgroundColor: "background.paper",
        borderBottom: `1px solid ${gray[200]}`,
        boxShadow: "none",
        zIndex: 50,
        flexShrink: 0
      },
      children: /* @__PURE__ */ jsxs(
        Toolbar,
        {
          sx: {
            justifyContent: "space-between",
            alignItems: "center",
            px: { mobile: 1.5, tablet: 4 },
            height: HEADER_HEIGHT,
            gap: 1
          },
          children: [
            /* @__PURE__ */ jsxs(
              Stack,
              {
                direction: "row",
                alignItems: "center",
                gap: { mobile: 0.5, tablet: 1 },
                sx: { minWidth: 0, flexShrink: 1 },
                children: [
                  isMobile && onMenuToggle && /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      onClick: onMenuToggle,
                      "aria-label": "open menu",
                      sx: { color: gray[500], p: 0.5 },
                      children: /* @__PURE__ */ jsx(MenuIcon, {})
                    }
                  ),
                  showCorporateChange && /* @__PURE__ */ jsx(CorporateChangeButton, {}),
                  breadcrumbText && /* @__PURE__ */ jsxs(
                    Box,
                    {
                      sx: {
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "14px",
                        color: gray[500]
                      },
                      children: [
                        /* @__PURE__ */ jsx(HomeIcon, { sx: { fontSize: 16, color: gray[400] } }),
                        /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { color: gray[500] }, children: "/" }),
                        /* @__PURE__ */ jsx(
                          Typography,
                          {
                            variant: "body2",
                            sx: {
                              color: gray[900],
                              fontWeight: 500,
                              ...isMobile && {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "120px"
                              }
                            },
                            children: breadcrumbText
                          }
                        )
                      ]
                    }
                  ),
                  leftActions
                ]
              }
            ),
            /* @__PURE__ */ jsxs(
              Stack,
              {
                direction: "row",
                alignItems: "center",
                gap: { mobile: 0.5, tablet: 1 },
                sx: { flexShrink: 0 },
                children: [
                  showSessionTimer && /* @__PURE__ */ jsx(SessionTimer, { compact: true }),
                  rightActions,
                  showUserSearch && /* @__PURE__ */ jsx(UserSearch, { ...userSearchProps }),
                  useMobileCollapse ? /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsx(IconButton, { onClick: handleOpenMore, sx: { padding: 0.5 }, children: /* @__PURE__ */ jsx(MoreVertIcon, { sx: { width: 24, height: 24 } }) }),
                    /* @__PURE__ */ jsx(
                      Popover,
                      {
                        open: Boolean(moreAnchorEl),
                        anchorEl: moreAnchorEl,
                        onClose: handleCloseMore,
                        anchorOrigin: { vertical: "bottom", horizontal: "right" },
                        transformOrigin: { vertical: "top", horizontal: "right" },
                        children: /* @__PURE__ */ jsxs(
                          Box,
                          {
                            display: "flex",
                            alignItems: "center",
                            px: 0.5,
                            py: 0.5,
                            gap: 0.5,
                            children: [
                              showThemeSwitch && /* @__PURE__ */ jsx(ThemeChangeSwitch, { size: 32 }),
                              showLanguageChange && /* @__PURE__ */ jsx(LanguageChangeButton, { size: 32 }),
                              showAccountMenu && /* @__PURE__ */ jsx(
                                AccountMenu,
                                {
                                  showProfileHeader: accountMenuShowProfile,
                                  showCopyright,
                                  extraItems: accountMenuExtraItems,
                                  onOpenReleaseNotes,
                                  manuals
                                }
                              )
                            ]
                          }
                        )
                      }
                    )
                  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                    showThemeSwitch && /* @__PURE__ */ jsx(ThemeChangeSwitch, {}),
                    showLanguageChange && /* @__PURE__ */ jsx(LanguageChangeButton, {}),
                    userDisplayName && /* @__PURE__ */ jsxs(
                      Box,
                      {
                        sx: {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          lineHeight: 1
                        },
                        children: [
                          /* @__PURE__ */ jsx(
                            Typography,
                            {
                              variant: "body2",
                              sx: { fontWeight: 600, color: gray[900], lineHeight: 1 },
                              children: userDisplayName
                            }
                          ),
                          userRoleLabel && /* @__PURE__ */ jsx(
                            Typography,
                            {
                              variant: "small",
                              sx: { color: gray[500], lineHeight: 1, mt: 1 },
                              children: userRoleLabel
                            }
                          )
                        ]
                      }
                    ),
                    showAccountMenu && /* @__PURE__ */ jsx(
                      AccountMenu,
                      {
                        showProfileHeader: accountMenuShowProfile,
                        showCopyright,
                        extraItems: accountMenuExtraItems,
                        onOpenReleaseNotes,
                        manuals
                      }
                    )
                  ] })
                ]
              }
            )
          ]
        }
      )
    }
  );
}
export {
  LayoutHeader
};
